/**
 * Per-tenant persistence for the top-nav ("big Scout") popover session so a
 * refresh rehydrates the popover in its prior view with the same conversation.
 *
 * Uses localStorage rather than sessionStorage on purpose: on a hard refresh
 * Amplify reports `authStatus` as unauthenticated for a beat before the session
 * resolves, and `providers/token.tsx` calls `sessionStorage.clear()` whenever
 * unauthenticated — so sessionStorage is wiped on every reload during that boot
 * window. localStorage survives it.
 *
 * To avoid a stale chat resurfacing long after the user walked away (localStorage
 * has no per-tab/per-session lifetime), entries carry a `persistedAt` stamp and
 * expire after `SESSION_TTL_MS`.
 *
 * Keyed on tenantId so switching orgs reads a fresh session. Only real
 * (persisted) conversation ids are stored: an optimistic id can't be rehydrated
 * from the server, so we drop it before writing.
 */

import { isScoutOptimisticConversationId } from "../hooks/scoutConversationIds";

export type ScoutPopoverViewMode = "closed" | "open" | "minimized";

export type PersistedScoutPopoverSession = {
  conversationId: string | undefined;
  openedAt: Date;
  viewMode: ScoutPopoverViewMode;
  title: string | undefined;
};

const KEY_PREFIX = "scout:popover-session:";

// How long a persisted popover session stays eligible for rehydration. Long
// enough to survive refreshes and short breaks; short enough that an abandoned
// chat doesn't pop back open a day later.
const SESSION_TTL_MS = 8 * 60 * 60 * 1000;

function isStorageAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function storageKey(tenantId: string): string {
  return `${KEY_PREFIX}${tenantId}`;
}

function isViewMode(value: unknown): value is ScoutPopoverViewMode {
  return value === "closed" || value === "open" || value === "minimized";
}

export function readScoutPopoverSession(
  tenantId: string | undefined
): PersistedScoutPopoverSession | undefined {
  if (!tenantId || !isStorageAvailable()) return undefined;
  try {
    const raw = window.localStorage.getItem(storageKey(tenantId));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return undefined;
    }

    // Expire stale sessions so an abandoned chat doesn't rehydrate much later.
    const persistedAt =
      typeof parsed.persistedAt === "string" ? Date.parse(parsed.persistedAt) : NaN;
    if (Number.isNaN(persistedAt) || Date.now() - persistedAt > SESSION_TTL_MS) {
      window.localStorage.removeItem(storageKey(tenantId));
      return undefined;
    }

    const viewMode = isViewMode(parsed.viewMode) ? parsed.viewMode : "closed";
    const conversationId =
      typeof parsed.conversationId === "string" ? parsed.conversationId : undefined;
    const title = typeof parsed.title === "string" ? parsed.title : undefined;
    const parsedOpenedAt =
      typeof parsed.openedAt === "string" ? new Date(parsed.openedAt) : undefined;
    const openedAt =
      parsedOpenedAt && !Number.isNaN(parsedOpenedAt.getTime()) ? parsedOpenedAt : new Date();

    return { conversationId, openedAt, viewMode, title };
  } catch {
    return undefined;
  }
}

export function writeScoutPopoverSession(
  tenantId: string | undefined,
  session: PersistedScoutPopoverSession
): void {
  if (!tenantId || !isStorageAvailable()) return;
  try {
    // Never persist an optimistic id — it can't rehydrate from the server.
    const conversationId = isScoutOptimisticConversationId(session.conversationId)
      ? undefined
      : session.conversationId;

    // Nothing worth restoring: no active conversation and the popover is closed.
    if (!conversationId && session.viewMode === "closed") {
      window.localStorage.removeItem(storageKey(tenantId));
      return;
    }

    window.localStorage.setItem(
      storageKey(tenantId),
      JSON.stringify({
        conversationId,
        openedAt: session.openedAt.toISOString(),
        viewMode: session.viewMode,
        title: session.title,
        persistedAt: new Date().toISOString(),
      })
    );
  } catch {
    // Quota exceeded or storage disabled — non-fatal; the session just won't
    // survive a refresh.
  }
}

export function clearScoutPopoverSession(tenantId: string | undefined): void {
  if (!tenantId || !isStorageAvailable()) return;
  try {
    window.localStorage.removeItem(storageKey(tenantId));
  } catch {
    // Non-fatal.
  }
}
